// Benepack OMS · routes (sitemap §5 outline · 一期 vertical slice 仅落地 5 route)
// 完整 88 endpoint × route 矩阵留 Phase 2 D-4+ backlog
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Spin } from 'antd';

import { RequireAuth } from './RequireAuth';
import { PermGuardRoute } from './PermGuard';
import { MainLayout } from '@/components/MainLayout';

// lazy chunk · 减首屏 bundle
const LoginPage = lazy(() => import('@/pages/login'));
// 自助找回密码 (public · 无 JWT · 与 /login 同层级 · 不入 RequireAuth)
const ForgotPasswordPage = lazy(() => import('@/pages/login/forgot-password'));
const ResetPasswordPage = lazy(() => import('@/pages/login/reset-password'));
// 强制 TOTP 二次验证 (2FA WP · public · 两步登录第二步 / 强制 enroll · 不入 RequireAuth)
const VerifyTwoFactorPage = lazy(() => import('@/pages/login/verify-2fa'));
const EnrollTwoFactorPage = lazy(() => import('@/pages/login/enroll-2fa'));
// 账户安全 (2FA WP · 登录后自助管理 · /system/security)
const SecuritySettingsPage = lazy(() => import('@/pages/system/security'));
const Workbench = lazy(() => import('@/pages/workbench'));
const Forbidden = lazy(() => import('@/pages/errors/Forbidden'));
const NotFound = lazy(() => import('@/pages/errors/NotFound'));
const CustomerList = lazy(() => import('@/pages/master/customers/list'));
const CustomerDetail = lazy(() => import('@/pages/master/customers/detail'));
const CustomerForm = lazy(() => import('@/pages/master/customers/form'));
// D-4.1 (v0.1-frontend-rc3)
const SupplierList = lazy(() => import('@/pages/master/suppliers/list'));
const SupplierDetail = lazy(() => import('@/pages/master/suppliers/detail'));
const SupplierForm = lazy(() => import('@/pages/master/suppliers/form'));
const ProductList = lazy(() => import('@/pages/master/products/list'));
const ProductDetail = lazy(() => import('@/pages/master/products/detail'));
const ProductForm = lazy(() => import('@/pages/master/products/form'));
const SkuList = lazy(() => import('@/pages/master/skus/list'));
const SkuDetail = lazy(() => import('@/pages/master/skus/detail'));
const SkuForm = lazy(() => import('@/pages/master/skus/form'));
const PriceListList = lazy(() => import('@/pages/master/price-lists/list'));
const PriceListDetail = lazy(() => import('@/pages/master/price-lists/detail'));
const PriceListForm = lazy(() => import('@/pages/master/price-lists/form'));
// P2-1 (PRD-2) · 定价三层接线 · 客户折扣维护 (list + create/edit form · 无 detail 页)
const CustomerDiscountList = lazy(() => import('@/pages/master/customer-discounts/list'));
const CustomerDiscountForm = lazy(() => import('@/pages/master/customer-discounts/form'));
// D-4.2c-1 (v0.1-frontend-rc7) · trade.Quotation vertical slice (list + detail 复合页 + create form)
const QuotationList = lazy(() => import('@/pages/trade/quotations/list'));
const QuotationDetail = lazy(() => import('@/pages/trade/quotations/detail'));
const QuotationForm = lazy(() => import('@/pages/trade/quotations/form'));
// D-4.2a (v0.1-frontend-rc4) · trade.SalesOrder 复合页 (header + lines + action bar)
const SalesOrderList = lazy(() => import('@/pages/trade/sales-orders/list'));
const SalesOrderForm = lazy(() => import('@/pages/trade/sales-orders/form'));
const SalesOrderDetail = lazy(() => import('@/pages/trade/sales-orders/detail'));
// D-4.2b-1 (v0.1-frontend-rc5b) · trade.SalesOrder 拆单复合页 (confirmed→拆单生成 PO)
const SalesOrderSplit = lazy(() => import('@/pages/trade/sales-orders/split'));
// D-4.2b-2 (v0.1-frontend-rc5a) · trade.PurchaseOrder 审批复合页 (header + lines + action bar)
const PurchaseOrderList = lazy(() => import('@/pages/trade/purchase-orders/list'));
const PurchaseOrderForm = lazy(() => import('@/pages/trade/purchase-orders/form'));
const PurchaseOrderDetail = lazy(() => import('@/pages/trade/purchase-orders/detail'));
// GL-14 (go-live P1) · 审批中心 (经理 PO 审批队列 · 点亮 Sidebar /procurement/approvals 死链)
const PurchaseOrderApprovalCenter = lazy(() => import('@/pages/trade/purchase-orders/approvals'));

// WP-07 (v0.1-frontend-rc9) · trade.Shipment 履约发货 vertical slice
const ShipmentList = lazy(() => import('@/pages/trade/shipments/list'));
const ShipmentDetail = lazy(() => import('@/pages/trade/shipments/detail'));
const ShipmentForm = lazy(() => import('@/pages/trade/shipments/form'));

// WP-08 (v0.1-frontend-rc10) · trade.ReceiveNote 履约收货 vertical slice
const ReceiveNoteList = lazy(() => import('@/pages/trade/receive-notes/list'));
const ReceiveNoteDetail = lazy(() => import('@/pages/trade/receive-notes/detail'));
const ReceiveNoteForm = lazy(() => import('@/pages/trade/receive-notes/form'));

// WP-14 (v0.1-frontend-rc13) · finance.Statement 对账单 vertical slice (第二条业务流)
const StatementList = lazy(() => import('@/pages/finance/statements/list'));
const StatementDetail = lazy(() => import('@/pages/finance/statements/detail'));
const StatementForm = lazy(() => import('@/pages/finance/statements/form'));

// WP-18 (v0.1-frontend-rc14) · finance.InvoiceRegister 发票登记 vertical slice (第二条业务流第 2 聚合)
const InvoiceRegisterList = lazy(() => import('@/pages/finance/invoice-registers/list'));
const InvoiceRegisterDetail = lazy(() => import('@/pages/finance/invoice-registers/detail'));
const InvoiceRegisterForm = lazy(() => import('@/pages/finance/invoice-registers/form'));

// AR (v0.1-frontend-rc18) · finance.AR 应收账款 vertical slice (第二条业务流第 3 聚合 · 收款侧)
const ARList = lazy(() => import('@/pages/finance/ar/list'));
const ARDetail = lazy(() => import('@/pages/finance/ar/detail'));
const ARForm = lazy(() => import('@/pages/finance/ar/form'));

// AP (v0.1-frontend-rc19) · finance.AP 应付账款 vertical slice (第二条业务流第 4 聚合 · 付款侧 · 完整业务链最后一片)
const APList = lazy(() => import('@/pages/finance/ap/list'));
const APDetail = lazy(() => import('@/pages/finance/ap/detail'));
const APForm = lazy(() => import('@/pages/finance/ap/form'));

// batch-slice (v0.1-frontend batch-slice) · master.Batch 批号台账 vertical slice (履约发货前置 · 解锁浏览器发货 step 5)
const BatchList = lazy(() => import('@/pages/fulfillment/batches/list'));
const BatchDetail = lazy(() => import('@/pages/fulfillment/batches/detail'));
const BatchForm = lazy(() => import('@/pages/fulfillment/batches/form'));
// coa-slice (v0.1-frontend) · fulfillment 域 · master.CoaReport 质检报告上传 (履约收货 qc_pass 前置 · 解锁 step 6)
const CoaList = lazy(() => import('@/pages/fulfillment/coa/list'));
const CoaForm = lazy(() => import('@/pages/fulfillment/coa/form'));

// audit-slice (v0.1-frontend-rc23) · platform 域 · AuditLog 审计日志只读纵切片 (点亮 Sidebar /platform/audit 占位)
const AuditList = lazy(() => import('@/pages/platform/audit/list'));
const AuditDetail = lazy(() => import('@/pages/platform/audit/detail'));
// emailjob-slice (前端补面④) · platform 域 · EmailJob 邮件作业只读纵切片 (点亮 Sidebar /platform/email 占位)
const EmailJobList = lazy(() => import('@/pages/platform/email/list'));
const EmailJobDetail = lazy(() => import('@/pages/platform/email/detail'));
// WP-B · platform 域 · 统一审批中心 (PO + 外发邮件共用审批队列 · 决策 #5)
const ApprovalCenter = lazy(() => import('@/pages/platform/approvals'));
// WP-D · platform 域 · 邮件签收看板 + 按类型签收 TTL 配置 (PDF 邮件确认签收)
const EmailReceiptDashboard = lazy(() => import('@/pages/platform/email/receipts'));
const EmailReceiptTtlSettings = lazy(() => import('@/pages/platform/email/receipt-ttl'));

// users-slice (前端补面② · v0.1-frontend-rc24) · system 域 · identity.User 管理纵切片 (点亮 Sidebar /system/users 占位)
const UserList = lazy(() => import('@/pages/system/users/list'));
const UserDetail = lazy(() => import('@/pages/system/users/detail'));
const UserForm = lazy(() => import('@/pages/system/users/form'));

// roles-slice (前端补面⑤ · 收官) · system 域 · identity.Role 只读纵切片 (点亮 Sidebar /system/roles 占位)
const RoleList = lazy(() => import('@/pages/system/roles/list'));
const RoleDetail = lazy(() => import('@/pages/system/roles/detail'));

// pdf-branding (go-live 反馈 #2) · system 域 · 企业信息 (公司抬头/品牌/Logo) admin 设置页
const CompanyProfileSettings = lazy(() => import('@/pages/system/settings/company-profile'));

// report-page (P2a Task 3 · v0.1-frontend-rc32) · 统计报表页 (消费 finance/trade /aggregate 端点 ·
// recharts 随本 chunk lazy 隔离 · xlsx 点导出才动态加载)
const ReportsPage = lazy(() => import('@/pages/reports'));

function withSuspense(node: React.ReactNode) {
  return (
    <Suspense
      fallback={
        <div style={{ height: '60vh', display: 'grid', placeItems: 'center' }}>
          <Spin />
        </div>
      }
    >
      {node}
    </Suspense>
  );
}

// router 类型显式标注 (WP-14 · tsc -b 声明 emit 修复 TS2742):
//   worktree 的 web/node_modules 是指向主仓的 symlink → 推断的 @remix-run/router 类型路径逃逸
//   worktree 根 ("not portable")。用 ReturnType<typeof createBrowserRouter> 经本地已导入符号命名 → portable。
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  { path: '/login', element: withSuspense(<LoginPage />) },
  // 自助找回密码 (public · 无 JWT)
  { path: '/forgot-password', element: withSuspense(<ForgotPasswordPage />) },
  { path: '/reset-password', element: withSuspense(<ResetPasswordPage />) },
  // 强制 TOTP 二次验证 (2FA WP · public · 不入 RequireAuth · pre-2FA 态由 AuthProvider 内存持有)
  { path: '/login/verify-2fa', element: withSuspense(<VerifyTwoFactorPage />) },
  { path: '/login/enroll-2fa', element: withSuspense(<EnrollTwoFactorPage />) },
  { path: '/forbidden', element: withSuspense(<Forbidden />) },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            index: true,
            element: withSuspense(<Workbench />),
            handle: { titleI18n: 'menu.dashboard' },
          },
          // report-page (P2a Task 3) · 统计报表 · 任一指标 read 权限即可进 (卡片级再 hasPerm 细分 ·
          // 5 perm 与 Sidebar permAny 同码 · 对 permission_seed.sql 核实)
          {
            path: 'reports',
            element: (
              <PermGuardRoute
                anyOf={[
                  'ar:read',
                  'ap:read',
                  'invoice_register:read',
                  'sales_order:read',
                  'purchase_order:read',
                ]}
              />
            ),
            handle: { titleI18n: 'menu.reports' },
            children: [{ index: true, element: withSuspense(<ReportsPage />) }],
          },
          {
            path: 'master',
            handle: { titleI18n: 'menu.master' },
            children: [
              { index: true, element: <Navigate to="/master/customers" replace /> },
              {
                path: 'customers',
                element: <PermGuardRoute perm="customer:read" />,
                handle: { titleI18n: 'menu.masterCustomers' },
                children: [
                  { index: true, element: withSuspense(<CustomerList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="customer:create" />,
                    handle: { title: '新建客户' },
                    children: [
                      { index: true, element: withSuspense(<CustomerForm mode="create" />) },
                    ],
                  },
                  {
                    path: ':id',
                    handle: { title: '客户详情' },
                    children: [
                      { index: true, element: withSuspense(<CustomerDetail />) },
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="customer:update" />,
                        handle: { title: '编辑客户' },
                        children: [
                          { index: true, element: withSuspense(<CustomerForm mode="edit" />) },
                        ],
                      },
                    ],
                  },
                ],
              },
              // D-4.1 (v0.1-frontend-rc3) · master 剩 4 entity vertical slice
              {
                path: 'suppliers',
                element: <PermGuardRoute perm="supplier:read" />,
                handle: { titleI18n: 'menu.masterSuppliers' },
                children: [
                  { index: true, element: withSuspense(<SupplierList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="supplier:create" />,
                    handle: { title: '新建供应商' },
                    children: [
                      { index: true, element: withSuspense(<SupplierForm mode="create" />) },
                    ],
                  },
                  {
                    path: ':id',
                    handle: { title: '供应商详情' },
                    children: [
                      { index: true, element: withSuspense(<SupplierDetail />) },
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="supplier:update" />,
                        handle: { title: '编辑供应商' },
                        children: [
                          { index: true, element: withSuspense(<SupplierForm mode="edit" />) },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'products',
                element: <PermGuardRoute perm="product:read" />,
                handle: { titleI18n: 'menu.masterProducts' },
                children: [
                  { index: true, element: withSuspense(<ProductList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="product:write" />,
                    handle: { title: '新建产品' },
                    children: [
                      { index: true, element: withSuspense(<ProductForm mode="create" />) },
                    ],
                  },
                  {
                    path: ':id',
                    handle: { title: '产品详情' },
                    children: [
                      { index: true, element: withSuspense(<ProductDetail />) },
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="product:write" />,
                        handle: { title: '编辑产品' },
                        children: [
                          { index: true, element: withSuspense(<ProductForm mode="edit" />) },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'skus',
                element: <PermGuardRoute perm="sku:read" />,
                handle: { titleI18n: 'menu.masterSkus' },
                children: [
                  { index: true, element: withSuspense(<SkuList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="sku:write" />,
                    handle: { title: '新建 SKU' },
                    children: [{ index: true, element: withSuspense(<SkuForm mode="create" />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: 'SKU 详情' },
                    children: [
                      { index: true, element: withSuspense(<SkuDetail />) },
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="sku:write" />,
                        handle: { title: '编辑 SKU' },
                        children: [{ index: true, element: withSuspense(<SkuForm mode="edit" />) }],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'price-lists',
                element: <PermGuardRoute perm="price_list:read" />,
                handle: { titleI18n: 'menu.masterPriceLists' },
                children: [
                  { index: true, element: withSuspense(<PriceListList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="price_list:create" />,
                    handle: { title: '新建价格表' },
                    children: [
                      { index: true, element: withSuspense(<PriceListForm mode="create" />) },
                    ],
                  },
                  {
                    path: ':id',
                    handle: { title: '价格表详情' },
                    children: [
                      { index: true, element: withSuspense(<PriceListDetail />) },
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="price_list:update" />,
                        handle: { title: '编辑价格表' },
                        children: [
                          { index: true, element: withSuspense(<PriceListForm mode="edit" />) },
                        ],
                      },
                    ],
                  },
                ],
              },
              // P2-1 (PRD-2) · 客户折扣维护 (customer_discount:read / :write · 粗粒度 write · 无 detail 页)
              {
                path: 'customer-discounts',
                element: <PermGuardRoute perm="customer_discount:read" />,
                handle: { titleI18n: 'menu.masterCustomerDiscounts' },
                children: [
                  { index: true, element: withSuspense(<CustomerDiscountList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="customer_discount:write" />,
                    handle: { title: '新建客户折扣' },
                    children: [
                      { index: true, element: withSuspense(<CustomerDiscountForm mode="create" />) },
                    ],
                  },
                  {
                    path: ':id',
                    handle: { title: '客户折扣' },
                    children: [
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="customer_discount:write" />,
                        handle: { title: '编辑客户折扣' },
                        children: [
                          { index: true, element: withSuspense(<CustomerDiscountForm mode="edit" />) },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // D-4.2a (v0.1-frontend-rc4) · trade 域 · SalesOrder vertical slice
          {
            path: 'trade',
            handle: { titleI18n: 'menu.sales' },
            children: [
              { index: true, element: <Navigate to="/trade/sales-orders" replace /> },
              // D-4.2c-1 (v0.1-frontend-rc7) · trade.Quotation vertical slice (销售线头)
              {
                path: 'quotations',
                element: <PermGuardRoute perm="quotation:read" />,
                handle: { titleI18n: 'menu.salesQuotations' },
                children: [
                  { index: true, element: withSuspense(<QuotationList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="quotation:create" />,
                    handle: { title: '新建报价单' },
                    children: [{ index: true, element: withSuspense(<QuotationForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '报价单详情' },
                    children: [
                      { index: true, element: withSuspense(<QuotationDetail />) },
                      // P2-7 (UX-7): 草稿编辑 · 复用 QuotationForm 双模式 (:id 存在 → edit)
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="quotation:update" />,
                        handle: { title: '编辑报价单' },
                        children: [{ index: true, element: withSuspense(<QuotationForm />) }],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'sales-orders',
                element: <PermGuardRoute perm="sales_order:read" />,
                handle: { titleI18n: 'menu.salesOrders' },
                children: [
                  { index: true, element: withSuspense(<SalesOrderList />) },
                  // WP-灵活入口 · 手工直建 SO（不经报价派生 · sales_order:create 守门）
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="sales_order:create" />,
                    handle: { title: '新建销售订单' },
                    children: [{ index: true, element: withSuspense(<SalesOrderForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '销售订单详情' },
                    children: [
                      { index: true, element: withSuspense(<SalesOrderDetail />) },
                      // D-4.2b-1 · 拆单复合页 (procurement 角色 · sales_order:split 守门)
                      {
                        path: 'split',
                        element: <PermGuardRoute perm="sales_order:split" />,
                        handle: { title: '销售订单拆单' },
                        children: [{ index: true, element: withSuspense(<SalesOrderSplit />) }],
                      },
                    ],
                  },
                ],
              },
              // D-4.2b-2 (v0.1-frontend-rc5a) · trade.PurchaseOrder 审批 vertical slice
              {
                path: 'purchase-orders',
                element: <PermGuardRoute perm="purchase_order:read" />,
                handle: { titleI18n: 'menu.procurementPo' },
                children: [
                  { index: true, element: withSuspense(<PurchaseOrderList />) },
                  // WP-灵活入口 · 手工直建 PO（不经 SO 拆单 · purchase_order:create 守门）
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="purchase_order:create" />,
                    handle: { title: '新建采购订单' },
                    children: [{ index: true, element: withSuspense(<PurchaseOrderForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '采购订单详情' },
                    children: [{ index: true, element: withSuspense(<PurchaseOrderDetail />) }],
                  },
                ],
              },
              // WP-07 (v0.1-frontend-rc9) · trade.Shipment 履约发货 vertical slice
              {
                path: 'shipments',
                element: <PermGuardRoute perm="shipment:read" />,
                handle: { titleI18n: 'menu.fulfillmentShipments' },
                children: [
                  { index: true, element: withSuspense(<ShipmentList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="shipment:create" />,
                    handle: { title: '新建发货单' },
                    children: [{ index: true, element: withSuspense(<ShipmentForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '发货单详情' },
                    children: [{ index: true, element: withSuspense(<ShipmentDetail />) }],
                  },
                ],
              },
              // WP-08 (v0.1-frontend-rc10) · trade.ReceiveNote 履约收货 vertical slice
              {
                path: 'receive-notes',
                element: <PermGuardRoute perm="receive_note:read" />,
                handle: { titleI18n: 'menu.fulfillmentReceiveNotes' },
                children: [
                  { index: true, element: withSuspense(<ReceiveNoteList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="receive_note:create" />,
                    handle: { title: '新建收货单' },
                    children: [{ index: true, element: withSuspense(<ReceiveNoteForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '收货单详情' },
                    children: [{ index: true, element: withSuspense(<ReceiveNoteDetail />) }],
                  },
                ],
              },
            ],
          },
          // GL-14 (go-live P1) · 审批中心 · 点亮 Sidebar /procurement/approvals 死链 (菜单项已存在 · 原 404)
          // ★ perm = purchase_order:approve (逐字对齐 Sidebar permAny · permission_seed.sql:89 真 perm ·
          //   manager+admin :226)。procurement 组仅此一页走 /procurement/* (PO 列表在 /trade/purchase-orders)
          //   → 扁平注册单条 route · 不建 procurement 父组 (裸 /procurement 保持 NotFound · 与现状一致)。
          {
            path: 'procurement/approvals',
            element: <PermGuardRoute perm="purchase_order:approve" />,
            handle: { titleI18n: 'menu.procurementApprovals' },
            children: [{ index: true, element: withSuspense(<PurchaseOrderApprovalCenter />) }],
          },
          // batch-slice (v0.1-frontend) · fulfillment 域 · master.Batch 批号台账 vertical slice
          // (履约发货前置 · 解锁浏览器发货 step 5 · Sidebar 链 /fulfillment/batches 已存在 → 点亮)
          {
            path: 'fulfillment',
            handle: { titleI18n: 'menu.fulfillment' },
            children: [
              { index: true, element: <Navigate to="/fulfillment/batches" replace /> },
              {
                path: 'batches',
                element: <PermGuardRoute perm="batch:read" />,
                handle: { titleI18n: 'menu.fulfillmentBatches' },
                children: [
                  { index: true, element: withSuspense(<BatchList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="batch:write" />,
                    handle: { title: '新建批号' },
                    children: [{ index: true, element: withSuspense(<BatchForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '批号详情' },
                    children: [{ index: true, element: withSuspense(<BatchDetail />) }],
                  },
                ],
              },
              // coa-slice (v0.1-frontend) · COA 质检报告上传 (履约收货 qc_pass 前置 · 解锁 step 6)
              {
                path: 'coa',
                element: <PermGuardRoute perm="coa:read" />,
                handle: { titleI18n: 'menu.fulfillmentCoa' },
                children: [
                  { index: true, element: withSuspense(<CoaList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="coa:upload" />,
                    handle: { title: '上传 COA' },
                    children: [{ index: true, element: withSuspense(<CoaForm />) }],
                  },
                ],
              },
            ],
          },
          // WP-14 (v0.1-frontend-rc13) · finance 域 · Statement 对账单 vertical slice (第二条业务流)
          {
            path: 'finance',
            handle: { titleI18n: 'menu.finance' },
            children: [
              { index: true, element: <Navigate to="/finance/statements" replace /> },
              {
                path: 'statements',
                element: <PermGuardRoute perm="statement:read" />,
                handle: { titleI18n: 'menu.financeStatements' },
                children: [
                  { index: true, element: withSuspense(<StatementList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="statement:write" />,
                    handle: { title: '新建对账单' },
                    children: [{ index: true, element: withSuspense(<StatementForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '对账单详情' },
                    children: [{ index: true, element: withSuspense(<StatementDetail />) }],
                  },
                ],
              },
              // WP-18 (v0.1-frontend-rc14) · finance.InvoiceRegister 发票登记 vertical slice
              {
                path: 'invoice-registers',
                element: <PermGuardRoute perm="invoice_register:read" />,
                handle: { titleI18n: 'menu.financeInvoiceRegisters' },
                children: [
                  { index: true, element: withSuspense(<InvoiceRegisterList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="invoice_register:write" />,
                    handle: { title: '新建发票登记' },
                    children: [{ index: true, element: withSuspense(<InvoiceRegisterForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '发票登记详情' },
                    children: [{ index: true, element: withSuspense(<InvoiceRegisterDetail />) }],
                  },
                ],
              },
              // AR (v0.1-frontend-rc18) · finance.AR 应收账款 vertical slice (第二条业务流第 3 聚合 · 收款侧)
              {
                path: 'ar',
                element: <PermGuardRoute perm="ar:read" />,
                handle: { titleI18n: 'menu.financeAr' },
                children: [
                  { index: true, element: withSuspense(<ARList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="ar:write" />,
                    handle: { title: '新建应收单' },
                    children: [{ index: true, element: withSuspense(<ARForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '应收单详情' },
                    children: [{ index: true, element: withSuspense(<ARDetail />) }],
                  },
                ],
              },
              // AP (v0.1-frontend-rc19) · finance.AP 应付账款 vertical slice (第二条业务流第 4 聚合 · 付款侧 · 完整业务链最后一片)
              {
                path: 'ap',
                element: <PermGuardRoute perm="ap:read" />,
                handle: { titleI18n: 'menu.financeAp' },
                children: [
                  { index: true, element: withSuspense(<APList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="ap:write" />,
                    handle: { title: '新建应付单' },
                    children: [{ index: true, element: withSuspense(<APForm />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '应付单详情' },
                    children: [{ index: true, element: withSuspense(<APDetail />) }],
                  },
                ],
              },
            ],
          },
          // audit-slice (v0.1-frontend-rc23) · platform 域 · AuditLog 审计日志只读纵切片
          // (点亮 Sidebar /platform/audit 占位 · 现 404 · RBAC audit_log:read = admin+manager)
          // ★ perm code 是 audit_log:read (alice 实测 JWT permissions 含 audit_log:read·无 audit:read) ·
          //   Sidebar 原写 'audit:read' 是 latent typo (菜单恒隐)·本片一并修正 (见 Sidebar.tsx)。
          {
            path: 'platform',
            handle: { titleI18n: 'menu.platform' },
            children: [
              { index: true, element: <Navigate to="/platform/audit" replace /> },
              {
                path: 'audit',
                element: <PermGuardRoute perm="audit_log:read" />,
                handle: { titleI18n: 'menu.platformAudit' },
                children: [
                  { index: true, element: withSuspense(<AuditList />) },
                  {
                    path: ':id',
                    handle: { title: '审计日志详情' },
                    children: [{ index: true, element: withSuspense(<AuditDetail />) }],
                  },
                ],
              },
              // emailjob-slice (前端补面④) · /platform/email · RBAC email_job:read = admin+manager
              // (Sidebar /platform/email 占位已带 permAny:['email_job:read'] · 本片点亮 · 现 404)
              {
                path: 'email',
                element: <PermGuardRoute perm="email_job:read" />,
                handle: { titleI18n: 'menu.platformEmail' },
                children: [
                  { index: true, element: withSuspense(<EmailJobList />) },
                  // WP-D · 邮件签收看板 (静态段须排在 :id 前 · 否则被动态段吞 · RR6 静态优先但显式置前更稳)
                  {
                    path: 'receipts',
                    handle: { titleI18n: 'menu.platformEmailReceipts' },
                    children: [{ index: true, element: withSuspense(<EmailReceiptDashboard />) }],
                  },
                  // WP-D · 签收 TTL 配置 (读 email_job:read · 改 company_profile:write 页内 hasPerm 守门)
                  {
                    path: 'receipt-ttl',
                    handle: { titleI18n: 'menu.platformEmailReceiptTtl' },
                    children: [{ index: true, element: withSuspense(<EmailReceiptTtlSettings />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '邮件作业详情' },
                    children: [{ index: true, element: withSuspense(<EmailJobDetail />) }],
                  },
                ],
              },
              // WP-B · 统一审批中心 (/platform/approvals · PO + 外发邮件共用 · 决策 #5) ·
              //   ★ anyOf 三码: 邮件审批人 (Eileen · 持 approval:approve) 与 PO 审批人 (purchase_order:approve)
              //     均可进 · 不被弹 /forbidden (新 PO 审批中心仍在 /procurement/approvals 不动)。
              {
                path: 'approvals',
                element: (
                  <PermGuardRoute
                    anyOf={['approval:approve', 'approval:reject', 'purchase_order:approve']}
                  />
                ),
                handle: { titleI18n: 'menu.platformApprovals' },
                children: [{ index: true, element: withSuspense(<ApprovalCenter />) }],
              },
            ],
          },
          // users-slice (前端补面② · v0.1-frontend-rc24) · system 域 · identity.User 管理纵切片
          // (点亮 Sidebar /system/users 占位 · 现 404 · RBAC user:read/create/update/disable + user_role:assign = admin)
          // ★ Sidebar /system/users 原写 'user:read' 经核实是真 perm (permission_seed.sql:121 · 非 typo) → 不动 Sidebar。
          {
            path: 'system',
            handle: { titleI18n: 'menu.system' },
            children: [
              { index: true, element: <Navigate to="/system/users" replace /> },
              {
                path: 'users',
                element: <PermGuardRoute perm="user:read" />,
                handle: { titleI18n: 'menu.systemUsers' },
                children: [
                  { index: true, element: withSuspense(<UserList />) },
                  {
                    path: 'new',
                    element: <PermGuardRoute perm="user:create" />,
                    handle: { title: '新建用户' },
                    children: [{ index: true, element: withSuspense(<UserForm mode="create" />) }],
                  },
                  {
                    path: ':id',
                    handle: { title: '用户详情' },
                    children: [
                      { index: true, element: withSuspense(<UserDetail />) },
                      {
                        path: 'edit',
                        element: <PermGuardRoute perm="user:update" />,
                        handle: { title: '编辑用户' },
                        children: [
                          { index: true, element: withSuspense(<UserForm mode="edit" />) },
                        ],
                      },
                    ],
                  },
                ],
              },
              // roles-slice (前端补面⑤ · 收官) · /system/roles · RBAC role:read = admin/manager
              // (Sidebar /system/roles 占位已带 permAny:['role:read'] · 本片点亮 · 现 404)
              {
                path: 'roles',
                element: <PermGuardRoute perm="role:read" />,
                handle: { titleI18n: 'menu.systemRoles' },
                children: [
                  { index: true, element: withSuspense(<RoleList />) },
                  {
                    path: ':id',
                    handle: { title: '角色详情' },
                    children: [{ index: true, element: withSuspense(<RoleDetail />) }],
                  },
                ],
              },
              // pdf-branding (go-live 反馈 #2) · /system/settings · 企业信息 (公司抬头/品牌) admin 设置
              // RBAC company_profile:write = admin (设置页可读可改 · perm 用 write 守门)。
              {
                path: 'settings',
                element: <PermGuardRoute perm="company_profile:write" />,
                handle: { titleI18n: 'menu.systemSettings' },
                children: [{ index: true, element: withSuspense(<CompanyProfileSettings />) }],
              },
              // 账户安全 (2FA WP · 自助管理二次验证) · 任一已登录用户可进 (无特定 perm·管自己的 2FA)。
              // 不挂 PermGuardRoute → 仅靠外层 RequireAuth 守门 (与其它系统页不同·这是个人安全设置)。
              {
                path: 'security',
                handle: { titleI18n: 'menu.systemSecurity' },
                children: [{ index: true, element: withSuspense(<SecuritySettingsPage />) }],
              },
            ],
          },
          { path: '*', element: withSuspense(<NotFound />) },
        ],
      },
    ],
  },
]);
